import React from 'react';
import { CardSectionRate } from '../../CardSectionRate/CardSectionRate';
import SectionSubtitle from '@/components/Typography/SectionSubtitle';
import { TextSummaryBox } from '@/components/TextSummaryBox/TextSummaryBox';
import { InspectionList } from '@/components/InspectionList';
import VisualAssessmentBox from '@/components/VisualAssessmentBox/VisualAssessmentBox';
import { CarDetailsAssessment, AssessmentItem } from '@/components/CarDetailsAssessment';
import { InspectionItem } from '@/components/InspectionList/InspectionList.types';
import { RatingSection } from '@/api/mappers/mapper-types';
import { CardSectionRateWide, SectionShortcutButton, SectionSummary, } from '@/components';
import { PhotoGallery } from '@/components/PhotoGallery';
import type { PhotoGalleryItem } from '@/components/PhotoGallery/PhotoGallery.types';
import { SectionNoData } from '@/components/SectionNoData';

interface CockpitSection {
  /** Lista elementów oceny kokpitu (plastiki, drewno, elementy metalowe itp.) */
  assessmentItems: AssessmentItem[];
  /** Tytuł podsumowania rzeczoznawcy dotyczącego kokpitu */
  summaryTitle: string;
  /** Treść podsumowania rzeczoznawcy dotyczącego stanu elementów kokpitu */
  summaryContent: string | null;
}

interface InteriorSection {
  /** Ocena wizualna stanu elementów wnętrza (1-4) */
  visualRating: 1 | 2 | 3 | 4 | null;
  /** Lista elementów oceny wnętrza (zabrudzenia, zarysowania, pęknięcia itp.) */
  assessmentItems: AssessmentItem[];
  /** Tytuł podsumowania rzeczoznawcy dotyczącego wnętrza */
  summaryTitle: string;
  /** Treść podsumowania rzeczoznawcy dotyczącego stanu elementów wnętrza */
  summaryContent: string | null;
}

interface RaportCarInnerProps {
  /** Sekcja oceny kontroli wnętrza z tytułem, ratingiem i opisem */
  ratingSection: RatingSection;
  /** Lista uwag dotyczących ekspertyzy wnętrza */
  remarks: string[];
  /** Sekcja oceny elementów kokpitu */
  cockpitSection: CockpitSection;
  /** Sekcja oceny elementów wnętrza */
  interiorSection: InteriorSection;
  /** Lista elementów wyposażenia pojazdu do weryfikacji sprawności */
  equipmentItems: InspectionItem[];
  /** Podsumowanie rzeczoznawcy dotyczące wyposażenia */
  equipmentExpertSummary: string | null;
  /** Galeria zdjęć wnętrza */
  galleryPhotos?: PhotoGalleryItem[];
}

export default function RaportCarInner({
  ratingSection,
  remarks,
  cockpitSection,
  interiorSection,
  equipmentItems,
  equipmentExpertSummary,
  galleryPhotos,
}: RaportCarInnerProps) {
  const hasCockpitShortcut = cockpitSection.assessmentItems.length > 0;
  const hasInteriorShortcut = interiorSection.assessmentItems.length > 0;
  const hasEquipmentShortcut = equipmentItems.length > 0;
  const hasAnyShortcut =
    hasCockpitShortcut || hasInteriorShortcut || hasEquipmentShortcut;

  return (
    <>
      {hasAnyShortcut && (
        <div className="px-4 mt-10 md:hidden">
          <p className="text-sm font-semibold text-text-primary mb-4">
            Sprawdź raporty dostępne w tej sekcji:
          </p>
          <div className="flex flex-col gap-3">
            {hasCockpitShortcut && (
              <SectionShortcutButton
                label="Kokpit"
                targetId="stan-elementow-kokpitu"
              />
            )}
            {hasInteriorShortcut && (
              <SectionShortcutButton
                label="Wnętrze"
                targetId="stan-elementow-wnetrza"
              />
            )}
            {hasEquipmentShortcut && (
              <SectionShortcutButton
                label="Wyposażenie"
                targetId="wyposazenie-wnetrze"
              />
            )}
          </div>
        </div>
      )}

      <div className="mt-20 w-full" id="podsumowanie-ekspertyzy-wnetrza">
        <SectionSubtitle
          title="Podsumowanie ekspertyzy wnętrza"
          description="Ocena punktowa oraz zestawienie uwag."
        />

        <div className="flex flex-col gap-11 items-start mt-11 md:flex-row">
          {ratingSection.rating !== null ? (
            ratingSection.rating === 5 ?
              <CardSectionRateWide
                title={ratingSection.title}
                rating={ratingSection.rating}
                description={'Wygląda na to, że nasza ekspertyza nie wykazała żadnych nieprawidłowości w ocenie wnętrza.'}
                ratingMessage={ratingSection.ratingMessage}
              />
              : <>
                <CardSectionRate
                  title={ratingSection.title}
                  rating={ratingSection.rating}
                  description={ratingSection.description ?? undefined}
                  ratingMessage={ratingSection.ratingMessage}
                />
                {remarks.length > 0 && <TextSummaryBox title="Na to zwróć uwagę:" items={remarks} />}
              </>
          ) : (
            <SectionNoData
              title="Ocena wnętrza w przygotowaniu"
              description="Rzeczoznawca pracuje nad oceną wnętrza pojazdu. Dane będą dostępne wkrótce."
            />
          )}
        </div>
      </div>

      <div className="mt-20" id="stan-elementow-kokpitu">
        <SectionSubtitle
          title="Stan elementów kokpitu"
          description="Weryfikacja elementów kokpitu - sprawdzenie zużycia i uszkodzeń."
        />
        {cockpitSection.assessmentItems.length > 0 ? (
          <div className="mt-10">
            <CarDetailsAssessment
              assessmentItems={cockpitSection.assessmentItems}
              summaryTitle={cockpitSection.summaryTitle}
              summaryIcon='settings-gear'
              summaryContent={cockpitSection.summaryContent || undefined}
            />
          </div>
        ) : (
          <div className="mt-10">
            <SectionNoData
              title="Dane kokpitu w przygotowaniu"
              description="Informacje o stanie elementów kokpitu są w trakcie opracowywania."
            />
          </div>
        )}
      </div>

      <div className="mt-20" id="stan-elementow-wnetrza">
        <SectionSubtitle
          title="Stan elementów wnętrza"
          description="Weryfikacja elementów wnętrza – sprawdzenie zużycia i uszkodzeń."
        />

        {interiorSection.visualRating !== null && <VisualAssessmentBox className="mt-10" rating={interiorSection.visualRating} />}

        {interiorSection.assessmentItems.length > 0 ? (
          <div className="mt-10">
            <CarDetailsAssessment
              assessmentItems={interiorSection.assessmentItems}
              summaryTitle={interiorSection.summaryTitle}
              summaryContent={interiorSection.summaryContent || undefined}
              summaryIcon='car-seat'
            />
          </div>
        ) : (
          <div className="mt-10">
            <SectionNoData
              title="Dane wnętrza w przygotowaniu"
              description="Informacje o stanie elementów wnętrza są w trakcie opracowywania."
            />
          </div>
        )}
      </div>

      <div className="mt-20" id="wyposazenie-wnetrze">
        <SectionSubtitle
          title="Wyposażenie"
          description="Weryfikacja sprawności elementów wyposażenia pojazdu."
        />
        <InspectionList
          className="mt-10"
          items={equipmentItems}
          remarks={remarks}
          expertSummary={equipmentExpertSummary}
        />
        {equipmentExpertSummary && (
          <div className="mt-20 md:mt-0 md:px-8">
            <SectionSummary
              iconName="handshake"
              title="Podsumowanie Rzeczonawcy dotyczące stanu elementów wyposażenia"
              content={equipmentExpertSummary}
              className="md:border-t-0 md:rounded-t-none"
            />
          </div>
        )}
      </div>

      {/* Galeria wnętrza */}
      {galleryPhotos && galleryPhotos.length > 0 && (
        <div id="galeria-wnetrza" className="mt-12">
          <PhotoGallery
            photos={galleryPhotos}
            title="Galeria wnętrza pojazdu"
            iconName="trophy"
            collapsible={true}
            defaultExpanded={false}
            previewCount={4}
            galleryVariant="default"
          />
        </div>
      )}
    </>
  );
}
